import {
  ChatMessage,
  ChatService,
  ConversationParticipant,
  ConversationService,
  ConversationType,
  MessageType,
  TChatChannel,
} from '@talon/web/lib/api';
import { Anchor, Box, Button, Card, CardFooter, CardHeader, Collapsible, Text, TextInput } from 'grommet';
import { Add, Close, FormClose } from 'grommet-icons';
import { kebabCase } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useRef, useState } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ThemeContext } from 'styled-components';

import { InboxMessage, ITypingPayload, SupportChat } from '.';
import { ConfirmModal, IMentionCandidate, MessageList, StatusPill, UserInput } from '../../../components/ui';
import { useChatStore, useProjectStore, useTalentManagerStore, useUserStore } from '../../../context';
import { EmptyState } from '@talon/web/src/components/ui/EmptyState';
import { canLogTime, creditsAvailable } from '../../talent-manager/credits';

const RESERVED_MENTION_HANDLES: IMentionCandidate[] = [
  { id: 'bot', label: 'talonBot' },
  { id: 'support', label: 'talonSupport' },
];

// What kind of conversation the open one is, as a neutral chip beside its name
const CONVERSATION_KIND: Partial<Record<ConversationType, string>> = {
  [ConversationType.PROJECT]: 'Project',
  [ConversationType.TALENT_MANAGER]: 'Talent manager',
  [ConversationType.SUPPORT]: 'Support',
};

const MESSAGE_PAGE_SIZE = 30;
const TYPING_PING_THROTTLE_MS = 2500;
const TYPING_INDICATOR_TTL_MS = 4000;

export const ChatWidget = observer(() => {
  const [query, setQuery] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [activeChannelId, setActiveChannelId] = useState<string>();
  const [participants, setParticipants] = useState<ConversationParticipant[]>([]);
  const [messageState, setMessageState] = useState<{
    conversationId?: string;
    messages: Array<Partial<ChatMessage>>;
    pending?: string;
  }>({ messages: [] });
  const [messageToSend, setMessageToSend] = useState<Partial<ChatMessage>>();
  const [showChat, setShowChat] = useState<boolean>(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startingY, setStartingY] = useState(0);
  const [timerStartedAt, setTimerStartedAt] = useState<Date | null>(null);
  const [timerNote, setTimerNote] = useState('');
  const [timerElapsedMs, setTimerElapsedMs] = useState(0);
  const [loggingTime, setLoggingTime] = useState(false);
  const [messagePage, setMessagePage] = useState(1);
  const [messageTotal, setMessageTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Record<string, string>>({});
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const userStore = useUserStore();
  const tmStore = useTalentManagerStore();
  const chatStore = useChatStore();
  const projectStore = useProjectStore();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  const lastTypingSentAtRef = useRef(0);
  const lastSubmitRef = useRef<{ text: string; at: number } | null>(null);
  const typingTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const location = useLocation();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- styled-components types the theme as an empty DefaultTheme, not Grommet's theme
  const theme = useContext(ThemeContext as React.Context<any>);

  // Kept in the chat store so badges elsewhere (Message buttons, the launcher) share the unread counts
  const channels = chatStore.channels;
  const activeChannel = channels.find((c) => c.conversationId === activeChannelId);
  const activeEngagement = activeChannel?.engagementId
    ? (userStore.user?.is_talent_manager ? tmStore.myEngagements : tmStore.groupEngagements).find(
      (e) => e.id === activeChannel.engagementId
    )
    : null;
  const creditBalance = activeEngagement ? creditsAvailable(activeEngagement) : null;
  const isCreditWarning = creditBalance !== null && creditBalance > 0 && creditBalance < 30;
  const timerCreditLimitMinutes = Math.floor(Math.max(creditBalance ?? 0, 0) / 15) * 15;
  const timerNearCreditLimit =
    timerStartedAt && activeEngagement && timerElapsedMs >= Math.max(0, timerCreditLimitMinutes - 5) * 60_000;
  const timerOverCreditLimit = timerElapsedMs > timerCreditLimitMinutes * 60_000;
  const totalUnread = chatStore.totalUnread;
  const mentionCandidates: IMentionCandidate[] = [
    // A participant whose user was deleted comes back with no user, and can't be mentioned
    ...participants
      .filter((p) => p.user && p.user_id !== userStore.user?.id)
      .map((p) => ({ id: p.user_id, label: `${p.user.first_name} ${p.user.last_name}`.trim() })),
    ...RESERVED_MENTION_HANDLES,
  ];

  const fetchChannels = chatStore.fetchChannels;

  // Unread counts stay current: refreshed every half minute and whenever the window comes back
  useEffect(() => {
    if (!userStore.user?.id) return;
    if (userStore.user.is_talent_manager) tmStore.fetchMyEngagements();
    else tmStore.fetchGroupEngagements();
    fetchChannels();
    const interval = setInterval(fetchChannels, 30_000);
    window.addEventListener('focus', fetchChannels);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', fetchChannels);
    };
  }, [userStore.user?.id]);

  useEffect(() => {
    if (showChat) {
      fetchChannels();
      if (userStore.user?.is_talent_manager) tmStore.fetchMyEngagements();
    }
  }, [showChat]);

  useEffect(() => {
    if (dragRef.current) {
      setStartingY(dragRef.current.getBoundingClientRect().top);
    }
  }, [dragRef.current]);

  const sortMessagesAscending = (messages: Array<Partial<ChatMessage>>) =>
    messages.slice().sort((a, b) => {
      if (a.created_at && b.created_at) {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }
      return 0;
    });

  const selectChannel = async (channelId: string) => {
    setActiveChannelId(channelId);
    setMessagePage(1);
    setMessageTotal(0);
    setTypingUsers({});
    try {
      const [{ conversation }, { messages, total }] = await Promise.all([
        ConversationService.getConversation(channelId, 'participants'),
        ConversationService.listChatMessages(channelId, MESSAGE_PAGE_SIZE, 1),
      ]);
      setMessageState({ conversationId: channelId, messages: sortMessagesAscending(messages) });
      setMessageTotal(total);
      setParticipants(conversation.participants || []);
    } catch (error) {
      console.log('error loading channel history', error);
      setMessageState({ conversationId: channelId, messages: [] });
      setParticipants([]);
    }
    chatStore.markRead(channelId);
    ChatService.markChannelRead(channelId).catch((error) => console.log('error marking channel read', error));
  };

  const loadOlderMessages = async () => {
    if (!activeChannelId) return;
    setLoadingMore(true);
    try {
      const nextPage = messagePage + 1;
      const { messages: older } = await ConversationService.listChatMessages(
        activeChannelId,
        MESSAGE_PAGE_SIZE,
        nextPage
      );
      setMessageState((state) => ({ ...state, messages: [...sortMessagesAscending(older), ...state.messages] }));
      setMessagePage(nextPage);
    } catch (error) {
      console.log('error loading older messages', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!timerStartedAt) return;
    const interval = setInterval(() => setTimerElapsedMs(Date.now() - timerStartedAt.getTime()), 1000);
    return () => clearInterval(interval);
  }, [timerStartedAt]);

  const startTimer = () => {
    setTimerStartedAt(new Date());
    setTimerElapsedMs(0);
  };

  const stopAndLogTime = async () => {
    if (!timerStartedAt || !activeChannel?.engagementId) return;
    setLoggingTime(true);
    const logged = await tmStore.logManualTime({
      engagement_id: activeChannel.engagementId,
      category: 'Consultation',
      description: timerNote || 'Chat session',
      started_at: timerStartedAt.toISOString(),
      ended_at: new Date().toISOString(),
    });
    setLoggingTime(false);
    // Not saved (the error's been shown): keep the timer and note so nothing's lost
    if (!logged) return;
    setTimerStartedAt(null);
    setTimerNote('');
    toast.success('Time logged.');
  };

  const sendTypingPing = () => {
    if (!activeChannelId) return;
    const now = Date.now();
    if (now - lastTypingSentAtRef.current < TYPING_PING_THROTTLE_MS) return;
    lastTypingSentAtRef.current = now;
    setMessageToSend({ type: MessageType.TYPING, conversation_id: activeChannelId } as Partial<ChatMessage>);
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    sendTypingPing();
  };

  const handleTyping = (payload: ITypingPayload) => {
    if (payload.user_id === userStore.user?.id) return;
    if (typingTimeoutsRef.current[payload.user_id]) clearTimeout(typingTimeoutsRef.current[payload.user_id]);
    typingTimeoutsRef.current[payload.user_id] = setTimeout(() => {
      setTypingUsers((prev) => {
        const next = { ...prev };
        delete next[payload.user_id];
        return next;
      });
    }, TYPING_INDICATOR_TTL_MS);
    setTypingUsers((prev) => ({ ...prev, [payload.user_id]: payload.first_name }));
  };

  // Fired by SupportChat when an AI_MESSAGE frame lands — i.e. the bot's reply
  // (success or the API's fallback error message) has arrived. Clears the
  // composer's waiting indicator.
  const handleBotReply = () => {
    setLoading(false);
  };

  // Escape-to-cancel (FR-5): clear the waiting indicator and pending bubble, unlock the
  // composer empty, and fire the ephemeral CANCEL frame over the chat WS. The frame omits
  // message_id deliberately — under the single-flight invariant conversation_id identifies
  // the turn, and a late cancel (reply already landed) is a no-op server-side, so the
  // message simply stays rendered (AC-3).
  const handleCancel = () => {
    setLoading(false);
    setQuery('');
    if (!activeChannelId) return;
    setMessageToSend({ type: MessageType.CANCEL, conversation_id: activeChannelId } as Partial<ChatMessage>);
  };

  // Escape-to-cancel listens at the window level because the composer textarea is
  // `disabled` while a bot reply is in-flight (FR-1) — a disabled input can't receive key
  // events. Attached only while loading on a bot channel; a no-op otherwise (AC-4). The ref
  // keeps the listener's closure fresh without re-subscribing every render.
  const handleCancelRef = useRef(handleCancel);
  handleCancelRef.current = handleCancel;
  const isBotChannel = activeChannel?.conversationType === ConversationType.BOT;
  useEffect(() => {
    if (!loading || !isBotChannel) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCancelRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [loading, isBotChannel]);

  // Clear the waiting indicator when the active channel changes, so a pending
  // bot reply doesn't leave a different channel's composer disabled.
  useEffect(() => {
    setLoading(false);
  }, [activeChannelId]);

  const formatElapsed = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Deep-link: a "you were mentioned" email links to `?openChat=<conversationId>` since chat has no
  // page route of its own; pick it up once on load and strip it from the URL.
  useEffect(() => {
    const openChatId = new URLSearchParams(location.search).get('openChat');
    if (openChatId) {
      chatStore.openChannel(openChatId);
      navigate(location.pathname, { replace: true });
    }
  }, []);

  // Same mechanism drives notification clicks (NotificationsMenu calls chatStore.openChannel directly).
  useEffect(() => {
    if (!chatStore.pendingChannelId) return;
    const channelId = chatStore.pendingChannelId;
    chatStore.clearPendingChannel();
    setShowChat(true);
    (async () => {
      if (!channels.length) await fetchChannels();
      await selectChannel(channelId);
    })();
  }, [chatStore.pendingChannelId]);

  // Side-rail default: the first time the widget is opened with nothing already selected, land
  // on the current project's General channel instead of an empty right-hand panel. Never
  // overrides a channel the user (or one of the mechanisms above) already selected.
  useEffect(() => {
    if (!showChat || activeChannelId || !channels.length) return;
    const currentProjectChannel = channels.find(
      (c) => c.conversationType === ConversationType.PROJECT && c.projectId === projectStore.currentProject?.id
    );
    if (currentProjectChannel) selectChannel(currentProjectChannel.conversationId);
  }, [showChat, channels, activeChannelId, projectStore.currentProject?.id]);

  //handle form submission
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!activeChannelId) return;

    const text = query.trim();
    if (!text) return;

    // Guards against a message getting persisted twice from a single logical send (observed in
    // QA: two identical ChatMessage rows, same conversation, milliseconds apart — the exact
    // client-side trigger wasn't reproducible, but no legitimate flow sends identical text twice
    // within a second, so this is a safe, cheap backstop regardless of root cause).
    const now = Date.now();
    if (lastSubmitRef.current?.text === text && now - lastSubmitRef.current.at < 1000) return;
    lastSubmitRef.current = { text, at: now };

    // Show the waiting indicator only on bot chats — the LLM reply arrives back
    // as an AI_MESSAGE WS frame (on both success and error), which clears it via
    // the onReply callback wired to SupportChat below.
    if (activeChannel?.conversationType === ConversationType.BOT) {
      setLoading(true);
    }
    setQuery('');
    // Same optimistic-send protocol as SupportInbox: the row enters the transcript with a
    // localId + status, SupportChat strips the client-only fields and sends the payload, and
    // the server echo is matched back by client_id (matchInboxEcho) to flip the row to 'sent'.
    const localId = crypto.randomUUID();
    const message: InboxMessage = {
      type: MessageType.USER_MESSAGE,
      text,
      conversation_id: activeChannelId,
      created_at: new Date().toISOString(),
      localId,
      client_id: localId,
      status: 'sending',
    };
    setMessageState((state) => ({ ...state, messages: [...state.messages, message] }));
    setMessageToSend(message);
  }

  //prevent empty submissions
  const handleEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && query) {
      handleSubmit(e);
    } else if (e.key == 'Enter') {
      e.preventDefault();
    }
  };

  if (!userStore.user?.id) {
    return null;
  }

  const onStart = (e: DraggableEvent, data: DraggableData) => {
    setDragStartPos({ x: data.x, y: data.y });
  };

  const onStop = (e: DraggableEvent, data: DraggableData) => {
    const deltaX = Math.abs(data.x - dragStartPos.x);
    const deltaY = Math.abs(data.y - dragStartPos.y);
    if (deltaX < 5 || deltaY < 5) {
      const height = Number(theme?.global?.size?.large?.substring(0, theme?.global?.size?.large?.length - 2));
      // click
      if (!showChat) {
        if (Math.abs(startingY + data.y) - height < 0) {
          setShowChat(true);
          setPosition({ x: data.x, y: height - startingY });
        } else {
          setShowChat(true);
        }
      }
    }
  };

  const projectChannels = channels.filter((c) => c.conversationType === ConversationType.PROJECT);
  const tmChannels = channels.filter((c) => c.conversationType === ConversationType.TALENT_MANAGER);
  const botChannels = channels
    .filter((c) => c.conversationType === ConversationType.BOT)
    .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
  // A TM can have more than one Support channel (one per agency group they're engaged by), so this
  // must render all of them, not just the first.
  const supportChannels = channels.filter((c) => c.conversationType === ConversationType.SUPPORT);
  // Project channels first so each project's bucket lists its "General" channel above that
  // project's per-engagement TM channels.
  const channelsByProject = [...projectChannels, ...tmChannels].reduce<Record<string, TChatChannel[]>>((acc, c) => {
    const key = c.projectName ?? 'Other';
    acc[key] = acc[key] ? [...acc[key], c] : [c];
    return acc;
  }, {});

  const renderChannelRow = (channel: TChatChannel) => {
    const active = channel.conversationId === activeChannelId;
    return (
      <Box
        key={channel.conversationId}
        flex={false}
        direction="row"
        align="center"
        justify="between"
        gap="xsmall"
        round="6px"
        pad={{ vertical: '7px', horizontal: '8px' }}
        background={active ? 'brand-tint' : undefined}
        hoverIndicator={active ? undefined : 'surface-header'}
        aria-current={active ? 'true' : undefined}
        onClick={() => selectChannel(channel.conversationId)}
        style={{ cursor: 'pointer' }}
      >
        <Text
          size="14px"
          color={active ? 'brand' : 'text-heading'}
          weight={active || channel.unreadCount > 0 ? 600 : 'normal'}
        >
          {channel.name}
        </Text>
        {channel.unreadCount > 0 && (
          <Box background="brand" round pad={{ horizontal: '7px' }} flex={false}>
            <Text size="12px" color="white">
              {channel.unreadCount}
            </Text>
          </Box>
        )}
      </Box>
    );
  };

  const formatRelativeTime = (iso?: string): string => {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const mins = Math.floor((Date.now() - then) / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    return new Date(iso).toLocaleDateString();
  };

  const handleNewBotConversation = async () => {
    try {
      const { conversation } = await ChatService.createBotConversation();
      await fetchChannels();
      await selectChannel(conversation.id);
    } catch (error) {
      console.log('error creating bot conversation', error);
    }
  };

  const handleDeleteBotChannel = async (channelId: string) => {
    try {
      await ConversationService.deleteConversation(channelId);
      const refreshed = await fetchChannels();
      if (channelId === activeChannelId) {
        const nextBot = refreshed.find((c) => c.conversationType === ConversationType.BOT);
        if (nextBot) {
          await selectChannel(nextBot.conversationId);
        } else {
          setActiveChannelId(undefined);
          setMessageState({ messages: [] });
          setParticipants([]);
        }
      }
    } catch (error) {
      console.log('error deleting bot conversation', error);
    }
  };

  const renderBotChannelRow = (channel: TChatChannel) => (
    <Box
      key={channel.conversationId}
      direction="row"
      align="center"
      justify="between"
      pad={{ vertical: 'xsmall', horizontal: 'small' }}
      background={channel.conversationId === activeChannelId ? 'light-3' : undefined}
      hoverIndicator
      onClick={() => selectChannel(channel.conversationId)}
      style={{ cursor: 'pointer' }}
    >
      <Box direction="column" flex>
        <Text size="small" weight={channel.unreadCount > 0 ? 'bold' : 'normal'}>
          {channel.name}
        </Text>
        {channel.updatedAt && (
          <Text size="xsmall" color="dark-4">
            {formatRelativeTime(channel.updatedAt)}
          </Text>
        )}
      </Box>
      <Box direction="row" align="center" gap="xsmall">
        {channel.unreadCount > 0 && (
          <Box background="status-critical" round pad={{ horizontal: 'xsmall' }}>
            <Text size="xsmall" color="white">
              {channel.unreadCount}
            </Text>
          </Box>
        )}
        <Button
          plain
          hoverIndicator
          icon={<FormClose size="small" color="dark-4" />}
          onClick={(e) => {
            e?.stopPropagation();
            setConfirmDeleteId(channel.conversationId);
          }}
        />
      </Box>
    </Box>
  );

  // Support agents already have a dedicated inbox for these conversations — a launcher to start
  // a new chat with support has no purpose on the page built for support to answer them.
  return !(
    location.pathname.includes('/admin') ||
    location.pathname.includes('/onboarding') ||
    location.pathname.includes('/support-inbox')
  ) ? (
      <Draggable
        bounds="parent"
        handle=".handle"
        cancel=".chat-close"
        nodeRef={dragRef}
        onStart={onStart}
        onStop={onStop}
        position={position}
        onDrag={(e, data) => setPosition({ x: data.x, y: data.y })}
        axis="both"
      >
        <Box
          pad="small"
          round={true}
          style={{ position: 'fixed', bottom: '28px', right: '5%', zIndex: '10' }}
          ref={dragRef}
        >
          {!showChat && (
            <Box
              background="brand"
              style={{ cursor: 'move', position: 'relative', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)' }}
              round="999px"
              pad={{ vertical: '10px', horizontal: '18px' }}
              align="center"
              justify="center"
              direction="row"
              className="handle"
            >
              <Text size="15px" weight={600} color="white" style={{ cursor: 'pointer' }}>
                Messages
              </Text>
              {/* The one red badge: an alert outside the panel */}
              {totalUnread > 0 && (
                <Box
                  background="text-danger"
                  round="999px"
                  pad={{ horizontal: '6px' }}
                  border={{ color: 'white', size: '2px' }}
                  style={{ position: 'absolute', top: '-6px', right: '-6px' }}
                >
                  <Text size="12px" color="white">
                    {totalUnread}
                  </Text>
                </Box>
              )}
            </Box>
          )}
          <Collapsible direction="vertical" open={showChat}>
            <Card
              height="large"
              width="820px"
              background="white"
              border={{ color: 'border-card' }}
              elevation="none"
              round="12px"
              style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)' }}
            >
              <Box direction="column" height="100%">
                <CardHeader
                  pad={{ vertical: '12px', horizontal: '16px' }}
                  background="white"
                  border={{ side: 'bottom', color: 'border-divider' }}
                  style={{ cursor: 'move' }}
                  justify="between"
                  className="handle"
                  gap="small"
                >
                  <Text size="16px" weight={600} color="text-heading">
                    Messages
                  </Text>
                  <Button
                    className="chat-close"
                    plain
                    icon={<Close size="18px" color="text-secondary" />}
                    a11yTitle="Close messages"
                    onClick={() => setShowChat(false)}
                  />
                </CardHeader>
                <Box direction="row" flex>
                  <Box
                    width="180px"
                    flex={false}
                    overflow={{ vertical: 'auto' }}
                    background="surface-subtle"
                    border={{ side: 'right', color: 'border-divider' }}
                    pad={{ vertical: '8px', horizontal: '6px' }}
                    gap="2px"
                  >
                    <Box margin={{ top: 'xsmall' }}>
                      <Box direction="row" align="center" justify="between" pad={{ horizontal: 'small' }}>
                        <Text size="xsmall" weight="bold" color="dark-4">
                        Talon Bot
                        </Text>
                        <Button
                          plain
                          hoverIndicator
                          icon={<Add size="small" color="dark-4" />}
                          onClick={handleNewBotConversation}
                          title="New conversation"
                        />
                      </Box>
                      {botChannels.map(renderBotChannelRow)}
                    </Box>
                    {Object.entries(channelsByProject).map(([projectName, channelsInBucket]) => (
                      <Box key={projectName} flex={false} gap="2px">
                        <Text size="12px" color="text-label" margin={{ horizontal: '8px', top: '4px' }}>
                          {projectName}
                        </Text>
                        {channelsInBucket.map(renderChannelRow)}
                      </Box>
                    ))}
                    {/* Support is private to each person, started the first time they ask */}
                    <Box margin={{ top: 'small' }} flex={false} gap="2px">
                      {supportChannels.length > 0 ? (
                        supportChannels.map(renderChannelRow)
                      ) : (
                        <Box
                          flex={false}
                          round="6px"
                          pad={{ vertical: '7px', horizontal: '8px' }}
                          hoverIndicator="surface-header"
                          onClick={() => chatStore.openSupportChannel()}
                        >
                          <Text size="14px" color="brand" weight={600}>
                            Message support
                          </Text>
                        </Box>
                      )}
                    </Box>
                    {!channels.length && (
                      <EmptyState title="No conversations yet" body="Conversations you start or are added to appear here." />
                    )}
                  </Box>
                  <Box flex direction="column">
                    {!activeChannel ? (
                      <Box flex align="center" justify="center">
                        <Text size="small" color="text-meta">
                          Select a conversation
                        </Text>
                      </Box>
                    ) : (
                      <>
                        <Box
                          direction="row"
                          align="center"
                          gap="small"
                          pad={{ horizontal: '16px', vertical: '10px' }}
                          border={{ side: 'bottom', color: 'border-divider' }}
                        >
                          <Text weight={600} size="15px" color="text-heading">
                            {activeChannel.name}
                          </Text>
                          {CONVERSATION_KIND[activeChannel.conversationType] && (
                            <StatusPill label={CONVERSATION_KIND[activeChannel.conversationType] ?? ''} tone="neutral" />
                          )}
                        </Box>
                        {isCreditWarning && (
                          <Box background="pill-warning-bg" pad={{ horizontal: '16px', vertical: '6px' }}>
                            <Text size="13px" color="pill-warning-text">
                              {creditBalance}m of credits left ·{' '}
                              <Anchor
                                weight={600}
                                color="brand"
                                label="Buy more"
                                onClick={() =>
                                  navigate(`/project/${kebabCase(activeChannel.projectName ?? '')}/talent-manager`)
                                }
                              />
                            </Text>
                          </Box>
                        )}
                        {messagePage * MESSAGE_PAGE_SIZE < messageTotal && (
                          <Box align="center" pad={{ vertical: 'xsmall' }}>
                            <Button
                              size="small"
                              label={loadingMore ? 'Loading…' : 'Load earlier messages'}
                              disabled={loadingMore}
                              onClick={loadOlderMessages}
                            />
                          </Box>
                        )}
                        <MessageList messages={messageState.messages} pending={loading} />
                        {Object.keys(typingUsers).length > 0 && (
                          <Text size="xsmall" color="dark-4" margin={{ horizontal: 'small', vertical: 'xsmall' }}>
                            {Object.values(typingUsers).join(', ')} {Object.keys(typingUsers).length === 1 ? 'is' : 'are'}{' '}
                          typing…
                          </Text>
                        )}
                        <CardFooter direction="column" align="center" gap="none">
                          {activeChannel.showLogTime && (timerStartedAt || (activeEngagement && canLogTime(activeEngagement))) && (
                            <Box
                              direction="row"
                              align="center"
                              gap="small"
                              width="100%"
                              pad={{ horizontal: 'small', vertical: 'xsmall' }}
                              background="light-2"
                            >
                              {!timerStartedAt ? (
                                <Button size="small" label="Log time" onClick={startTimer} />
                              ) : (
                                <>
                                  <Text size="small" weight="bold">
                                    {formatElapsed(timerElapsedMs)}
                                  </Text>
                                  <TextInput
                                    size="small"
                                    placeholder="What did you work on? (optional)"
                                    value={timerNote}
                                    onChange={(e) => setTimerNote(e.target.value)}
                                  />
                                  <Button
                                    size="small"
                                    primary
                                    label={loggingTime ? 'Saving…' : 'Stop & log'}
                                    disabled={loggingTime}
                                    onClick={stopAndLogTime}
                                  />
                                </>
                              )}
                            </Box>
                          )}
                          {activeChannel.showLogTime && !timerStartedAt && activeEngagement && !canLogTime(activeEngagement) && (
                            <Text size="small" color="text-meta" margin={{ horizontal: 'small', vertical: 'xsmall' }}>
                              No credits left to log time. Ask the agency to buy more.
                            </Text>
                          )}
                          {activeChannel.showLogTime && timerNearCreditLimit && (
                            <Text size="small" color="text-danger" margin={{ horizontal: 'small', bottom: 'xsmall' }}>
                              {timerOverCreditLimit
                                ? 'This entry now exceeds the credits left and cannot be logged until more are bought.'
                                : `Stop before ${timerCreditLimitMinutes} minutes; longer entries exceed the credits left.`}
                            </Text>
                          )}
                          <SupportChat
                            setMessageState={setMessageState}
                            messageToSend={messageToSend}
                            conversationId={activeChannelId || ''}
                            onTyping={handleTyping}
                            onReply={handleBotReply}
                          />
                          {/* Admins observe agency↔talent manager conversations; the server drops their messages there */}
                          {userStore.user?.is_admin && activeChannel.conversationType === ConversationType.TALENT_MANAGER ? (
                            <Box
                              fill="horizontal"
                              pad={{ vertical: '12px', horizontal: '16px' }}
                              border={{ side: 'top', color: 'border-divider' }}
                            >
                              <Text size="13px" color="text-meta">
                                Admins can read talent manager conversations but not post in them.
                              </Text>
                            </Box>
                          ) : (
                            <Box direction="row" width="100%">
                              <UserInput
                                handleSubmit={handleSubmit}
                                loading={loading}
                                handleEnter={handleEnter}
                                textAreaRef={textAreaRef}
                                query={query}
                                setQuery={handleQueryChange}
                                mentionCandidates={mentionCandidates}
                                placeholder={`Message ${activeChannel.name}…`}
                              />
                            </Box>
                          )}
                        </CardFooter>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Card>
          </Collapsible>
          {confirmDeleteId && (
            <ConfirmModal
              content="Delete this conversation? All messages will be permanently removed."
              confirmLabel="Delete"
              onConfirm={() => {
                handleDeleteBotChannel(confirmDeleteId);
                setConfirmDeleteId(null);
              }}
              onCancel={() => setConfirmDeleteId(null)}
            />
          )}
        </Box>
      </Draggable>
    ) : null;
});
